/**
 * In-app "连接 Amazon": the onboarding authorization flow, framed as a dialog.
 *
 * `/onboarding` only admits a seller with no Shops, so a seller adding another
 * Amazon account from Settings or the Dashboard gets the same two-step
 * authorization and Shop selection here. It never closes by itself on
 * success — the seller reads the outcome (including a skipped Selling Partner
 * grant) and leaves with "完成".
 */
import { IconCircleCheck, IconInfoCircle } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  type ActivatedShops,
  AmazonConnectFlow,
  StepHeader,
} from "@/components/onboarding/amazon-connect-flow";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogPanel, DialogPopup, DialogTitle } from "@/components/ui/dialog";
import { fetchAuthUser, hydrateAuthUser } from "@/lib/auth-api";
import { useAuthStore } from "@/stores/auth";

interface ConnectAmazonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ConnectAmazonDialog({ open, onOpenChange }: ConnectAmazonDialogProps) {
  const { t } = useTranslation("settings");
  const queryClient = useQueryClient();

  // Refresh before the finish step shows, so the Shop list behind the dialog
  // already lists the new Shops by the time the seller clicks "完成" — or
  // closes the dialog any other way.
  async function handleActivated(data: ActivatedShops): Promise<void> {
    await fetchAuthUser().then(hydrateAuthUser, () => undefined);
    const first = data.shops[0];
    const store = useAuthStore.getState();
    // Keep the seller's current Shop; pick one only when there was none to keep.
    if (first && !store.currentShopId) store.setCurrentShop(first.id);
    await queryClient.invalidateQueries();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup
        className="sm:max-w-[480px]"
        closeProps={{ "aria-label": t("connectAmazonDialog.close") }}
      >
        <DialogTitle className="sr-only">{t("connectAmazonDialog.title")}</DialogTitle>
        <DialogPanel className="pt-8">
          {/* Mounted only while open, so every visit starts from step 1. */}
          <AmazonConnectFlow
            onActivated={handleActivated}
            renderFinish={({ shops, sellingPartner }) => (
              <>
                <StepHeader
                  icon={<IconCircleCheck className="size-5" />}
                  title={t("connectAmazonDialog.finishTitle")}
                  description={t("connectAmazonDialog.finishDescription", { count: shops.length })}
                />
                {sellingPartner === "skipped" ? (
                  <Alert variant="info">
                    <IconInfoCircle aria-hidden />
                    <AlertDescription>
                      {t("connectAmazonDialog.sellingPartnerSkipped")}
                    </AlertDescription>
                  </Alert>
                ) : null}
                <Button size="lg" className="mt-auto w-full" onClick={() => onOpenChange(false)}>
                  {t("connectAmazonDialog.done")}
                </Button>
              </>
            )}
          />
        </DialogPanel>
      </DialogPopup>
    </Dialog>
  );
}
